/**
 * 错误边界组件 - 捕获 React 组件树中的错误
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { errorHandler, formatErrorForDisplay } from '@foodbook/core-logic';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // 记录错误
    const appError = errorHandler.handleError(error, {
      componentStack: errorInfo.componentStack,
      location: window.location.href,
    });

    this.setState({ errorInfo });

    // 调用回调
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // 如果提供了自定义 fallback,使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 默认错误UI
      const errorDisplay = formatErrorForDisplay(this.state.error!);

      return (
        <div className="min-h-screen bg-amber-50 dark:bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-amber-100 dark:bg-gray-800 rounded-lg shadow-lg p-6 border-4 border-amber-700 dark:border-gray-600">
            {/* RPG风格的错误标题 */}
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">⚔️</div>
              <h1 className="text-2xl font-bold text-amber-900 dark:text-amber-100 mb-2 font-mono">
                哎呀！出错了
              </h1>
              <p className="text-amber-700 dark:text-amber-300 font-mono">
                冒险途中遇到了一些问题
              </p>
            </div>

            {/* 错误信息 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-4 mb-4 border-2 border-amber-300 dark:border-gray-600">
              <p className="text-gray-800 dark:text-gray-200 font-mono text-sm">
                {errorDisplay.message}
              </p>
              {errorDisplay.isDev && errorDisplay.details && (
                <details className="mt-3">
                  <summary className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200">
                    技术详情（开发者）
                  </summary>
                  <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 overflow-auto max-h-32">
                    {JSON.stringify(errorDisplay.details, null, 2)}
                  </pre>
                </details>
              )}
            </div>

            {/* 错误堆栈（仅开发环境） */}
            {errorDisplay.isDev && this.state.error?.stack && (
              <details className="mb-4">
                <summary className="text-sm text-amber-700 dark:text-amber-300 cursor-pointer hover:text-amber-900 dark:hover:text-amber-100">
                  查看错误堆栈
                </summary>
                <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 overflow-auto max-h-48 p-2 bg-white dark:bg-gray-900 rounded border border-gray-300 dark:border-gray-600">
                  {this.state.error.stack}
                </pre>
              </details>
            )}

            {/* 操作按钮 */}
            <div className="flex flex-col gap-3">
              <button
                onClick={this.handleReset}
                className="rpg-button-base w-full py-3 px-6 text-lg font-bold text-amber-900 dark:text-amber-100 hover:bg-amber-200 dark:hover:bg-gray-700 transition-colors"
              >
                🔄 重新开始
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="rpg-button-base w-full py-3 px-6 text-lg font-bold text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-gray-700 transition-colors"
              >
                🏠 返回主页
              </button>
            </div>

            {/* 错误日志信息 */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-500 font-mono">
                错误已记录 • 错误代码: {errorDisplay.code}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook: 用于函数组件中的错误处理
 */
export function useErrorHandler() {
  const handleError = (error: Error | unknown, context?: any) => {
    const appError = errorHandler.handleError(error, context);
    const errorDisplay = formatErrorForDisplay(appError);

    // 在开发环境下显示完整错误,生产环境显示友好信息
    if (errorDisplay.isDev) {
      console.error('[useErrorHandler]', appError);
    }

    return {
      error: appError,
      userMessage: errorDisplay.message,
      code: errorDisplay.code,
      details: errorDisplay.details,
    };
  };

  async function handleAsyncError<T>(operation: () => Promise<T>, context?: any) {
    try {
      const data = await operation();
      return [null, data];
    } catch (error) {
      return [handleError(error, context).error, null];
    }
  }

  return {
    handleError,
    handleAsyncError,
    errorHandler,
  };
}
